import { Box, Grid, IconButton, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import CollapseIcon from "@entrilia/shared/icons/CollapseIcon";
import ExpandIcon from "@entrilia/shared/icons/ExpandIcon";
import type {
  ReportAccessState,
  ReportKind,
  ReportSource,
  WorkbookFilter,
  WorkbookReportParameter,
  WorkbookRunStatus,
} from "../../../../../api/types/workbookRuns.types";
import useUserContext from "../../../../../context/useUserContext";
import WorkbookReportItemDate from "./WorkbookReportItemDate";
import WorkbookReportItemEndIcon from "./WorkbookReportItemEndIcon";
import WorkbookReportItemName from "./WorkbookReportItemName";
import WorkbookReportItemStartIcon from "./WorkbookReportItemStartIcon";
import WorkbookReportItemDetails from "./details/WorkbookReportItemDetails";

interface Props {
  clientCode: string;
  isLocked: boolean;
  reportName: string;
  reportAccessState: ReportAccessState;
  status: WorkbookRunStatus | undefined;
  lastChangedAt: string;
  workbookFilters: WorkbookFilter[];
  reportFilters: WorkbookReportParameter[];
  reportKind: ReportKind;
  reportSource: ReportSource;
  reportId: string;
  reportType: string;
  errorMessage: string | undefined;
  textColorForUpdating?: string;
  SheetName: React.ReactNode;
}
const WorkbookReportItem = ({
  clientCode,
  reportAccessState,
  isLocked,
  reportName,
  lastChangedAt,
  status,
  workbookFilters,
  reportFilters,
  reportKind,
  reportSource,
  reportId,
  reportType,
  errorMessage,
  textColorForUpdating,
  SheetName,
}: Props) => {
  const [expanded, setExpanded] = useState(false);
  const isDeleted = reportAccessState.isReportDeleted;
  const isReadonly = !reportAccessState.hasAccessToReport;
  const { clients } = useUserContext();

  const reportCompanyLogo = useMemo((): string | undefined => {
    const client = clients.find((c) => c.clientCode === clientCode);
    return client?.branding.logoMarkUrl;
  }, [clients, clientCode]);

  return (
    <Grid
      container
      sx={(t) => ({
        border: `1px solid ${t.palette.divider}`,
        borderRadius: "4px",
        bgcolor: t.palette.background.paper,
        width: "100%",
      })}
    >
      <Grid
        container
        sx={(t) => ({
          alignItems: "center",
          borderRadius: "4px",
          p: 1,
          "&:hover": {
            cursor: "pointer",
            bgcolor: t.palette.action.hover,
          },
          width: "100%",
          gap: 0.5,
        })}
        onClick={(evt) => {
          evt.stopPropagation();
          setExpanded((prev) => !prev);
        }}
      >
        <IconButton size="small" sx={(theme) => ({ p: 0.4, color: theme.palette.secondary.light })}>
          {expanded ? <CollapseIcon /> : <ExpandIcon />}
        </IconButton>
        <Stack
          direction={"row"}
          spacing={1}
          sx={{ flex: 1, alignItems: "center", justifyContent: "center", minWidth: 0 }}
        >
          <Box>
            <WorkbookReportItemStartIcon
              isDeleted={isDeleted}
              isReadonly={isReadonly}
              reportCompanyLogo={reportCompanyLogo}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <WorkbookReportItemName
              reportName={reportName}
              reportStatus={status}
              textColorForUpdating={textColorForUpdating}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, justifyContent: "space-between" }}>
              <WorkbookReportItemDate date={lastChangedAt} reportStatus={status} errorMessage={errorMessage} />
              {SheetName}
            </Box>
          </Box>
          <WorkbookReportItemEndIcon isLocked={isLocked} reportStatus={status} />
        </Stack>
      </Grid>
      <WorkbookReportItemDetails
        expanded={expanded}
        isReadonly={isReadonly}
        isDeleted={isDeleted}
        reportAccessState={reportAccessState}
        clientCode={clientCode}
        workbookFilters={workbookFilters}
        reportFilters={reportFilters}
        reportKind={reportKind}
        reportSource={reportSource}
        reportId={reportId}
        reportType={reportType}
      />
    </Grid>
  );
};

export default WorkbookReportItem;
